import Swal from "sweetalert2";
import {useAppStore} from "../store/app";
import {computed} from "vue";
import moment from "moment";
import {useAuthStore} from "@/store/auth";

export const useHelpers = () => {
    const TIER_ZERO = 0;
    const TIER_ONE = 1;
    const TIER_TWO = 2;

    const swalToastConfig = {
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        didOpen: (toast) => {
            toast.onmouseenter = Swal.stopTimer;
            toast.onmouseleave = Swal.resumeTimer;
        }
    }

    const errorToast = (title, message = '') => {
        Swal.fire({
            ...swalToastConfig,
            icon: "error",
            title: title,
            text: message,
        });
    }

    const successToast = (title, message = '') => {
        Swal.fire({
            ...swalToastConfig,
            icon: "success",
            title: title,
            text: message,
        });
    }

    const app = useAppStore();
    const auth = useAuthStore();

    const isNigeria = computed(() => {
        return app.country_abbr === "ng";
    });

    const makeQueryString = (params = {}, removeEmpty = false) => {
        if (removeEmpty) {
            params = Object.fromEntries(
                Object.entries(params).filter(
                    ([_, value]) => value !== undefined && value !== null && value !== "",
                ),
            );
        }
        return new URLSearchParams(params).toString();
    };
    const errorAlert = (errMessage, title) => {
        title = title || "There has been an error";
        Swal.fire(title, errMessage, "error");
    };

    const successAlert = (successMessage) => {
        Swal.fire("Success", successMessage, "success");
    };

    function debounce(func, timeout = 300) {
        let timer;
        return (...args) => {
            clearTimeout(timer);
            timer = setTimeout(() => {
                func.apply(this, args);
            }, timeout);
        };
    }

    const currencySign = computed(() => {
        return app.currencySign;
    });

    const currency = computed(() => {
        return app.currency;
    });

    const appName = computed(() => app.appName)
    const locale = isNigeria.value
        ? `en-${app.country_abbr}`
        : 'en'
    const getCurrencyFormatter = (currency = 'NGN', locale = 'en-NG', maximumFractionDigits = 2) => {

        return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency,
            maximumFractionDigits
        })
    }
    const currencyFormatter = getCurrencyFormatter(
        currency.value ?? 'NGN',
        locale
    )
    const numberToMoney = (value, currencyString = '', maximumFractionDigits = 2) => {
        if(currencyString) {
            return getCurrencyFormatter(currencyString, locale, maximumFractionDigits).format(value)
        }

        return getCurrencyFormatter(currency.value ?? 'NGN', locale, maximumFractionDigits).format(value)
    }

    const formatNumber = (value) => {
        if(!value) {
            return 0;
        }

        if(locale) {
            return new Intl.NumberFormat(locale).format(value)
        }

        return new Intl.NumberFormat('en-US').format(value)
    }

    const formatCompactNumber = (value) => {
        const num = Number(value || 0);
        const abNum = Math.abs(num);
        const sign = num < 0 ? '-' : '';

        let formattedNumber = abNum.toString();

        if (abNum >= 1e12) {
            formattedNumber = parseFloat((abNum / 1e12).toFixed(2)) + 'T';
        } else if (abNum >= 1e9) {
            formattedNumber = parseFloat((abNum / 1e9).toFixed(2)) + 'B';
        } else if (abNum >= 1e6) {
            formattedNumber = parseFloat((abNum / 1e6).toFixed(2)) + 'M';
        } else if (abNum >= 1e3) {
            formattedNumber = parseFloat((abNum / 1e3).toFixed(2)) + 'K';
        } else {
            formattedNumber = formatNumber(abNum);
        }

        return `${sign}${formattedNumber}`;
    }

    const isDefined = (value) => {
        return value !== "" && value !== undefined && value !== null;
    };

    const hasShopAddress = (shopAddress) => {
        return shopAddress !== "null" && isDefined(shopAddress);
    };

    const termsOfServiceUrl = computed(() => {
        return app.termsOfServiceUrl;
    });

    const getDateAfterDays = (n) => {
        return moment().add(n, 'days');
    }

    const datePickerFormatDate = (date) => {
        const day = date.getDate();
        const month = date.getMonth() + 1;
        const year = date.getFullYear();

        return `${day}/${month}/${year}`;
    };
    const datePickerFormat = (date) => {
        const [startDate, endDate] = date;

        if (startDate && endDate) {
            return `${datePickerFormatDate(startDate)} - ${datePickerFormatDate(endDate)}`;
        }

        return `${datePickerFormatDate(startDate)} - `;
    };

    const dateFormatStr = "yyyy-MM-dd"

    const delay = (time) => {
        return new Promise(resolve => setTimeout(resolve, time));
    }

    const ucFirst = (string) => {
        let strParts = string?.split(" ");
        strParts = strParts?.map(part => {
            return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
        });

        return strParts?.join(" ").trim();
    }

    const streaks = computed(() => {
        if (isNigeria.value) {
            return {
                0: 100000, 4: 125000, 5: 150000, 6: 200000, 7: 250000, 8: 300000, 9: 350000,
                10: 640000, 11: 1280000, 12: 2560000, 13: 5120000,
            }
        }

        return {
            0: 5000, 4: 10000, 5: 15000, 6: 20000, 7: 40000, 8: 80000,
            9: 120000, 10: 240000, 11: 480000, 12: 960000, 13: 1920000,
        };
    })

    const getStreak = (amount) => {
        const streak = streaks.value;
        return Object.keys(streak).find(key => amount == streak[key]);
    }

    const getCurrentStreak = (amount) => {
        const parsedAmount = Number(amount).toFixed(0);
        const streak = getStreak(parsedAmount);
        return streak ? Number(streak) : 0;
    }

    const getNextStreak = (amount) => {
        const parsedAmount = Number(amount).toFixed(0);
        if (parsedAmount === 0) {
            return 0;
        }

        const streak = getStreak(parsedAmount);
        if (parsedAmount > 0 && typeof streak === 'undefined') {
            return 1;
        }

        if (typeof streak === 'undefined') {
            return 0;
        }

        if (Number(streak) === 0) {
            return 4;
        }

        return Number(streak) === 13 ? Number(streak) : Number(streak) + 1;
    }

    const getStreakProgress = (amount, hasRepayment) => {
        const parsedAmount = Number(amount).toFixed(0);
        const currentStreak = hasRepayment ? getCurrentStreak(parsedAmount) : 0;
        const nextStreak = getNextStreak(parsedAmount);

        return `${currentStreak} / ${nextStreak}`;
    }

    const getNextAmount = (amount) => {
        const parsedAmount = Number(amount).toFixed(0);
        const streak = streaks.value;
        const currentStreak = getCurrentStreak(parsedAmount);
        const nextStreak = getNextStreak(parsedAmount);

        if (Number(parsedAmount) !== Number(streak[currentStreak])){
            return streak[0];
        }


        return streak[nextStreak] ? streak[nextStreak] : streak[13];
    }

    const formatDate = (value, format = "DD/MM/YYYY HH:mm:ss") => {
        return moment(value).format(format);
    }

    const hasAnyRole = (roleSlugs = [], user = auth.user) => {
        return user?.roles?.some(role => roleSlugs.includes(role.slug));
    }

    return {
        TIER_ZERO,
        TIER_ONE,
        TIER_TWO,
        makeQueryString,
        errorAlert,
        successAlert,
        debounce,
        numberToMoney,
        formatNumber,
        formatCompactNumber,
        appName,
        isNigeria,
        isDefined,
        hasShopAddress,
        termsOfServiceUrl,
        currencySign,
        currency,
        currencyFormatter,
        getDateAfterDays,
        datePickerFormatDate,
        datePickerFormat,
        dateFormatStr,
        errorToast,
        successToast,
        delay,
        ucFirst,
        streaks,
        getStreak,
        getCurrentStreak,
        getNextStreak,
        getStreakProgress,
        getNextAmount,
        formatDate,
        hasAnyRole,
    }
}
